import { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useToast } from "hooks/toast";

import { hideLoading, showLoading } from "store/modules/loading/actions";

import { AdminArea } from "components/Body/AdminArea";
import { Drawer } from "components/Drawer";

import { hearingFileTypeAttendantRole } from "services/hearingFileTypeAttendantRole";

import { LogOperation } from "utils/entities/logOperation";
import { FORMS_ADMIN } from "utils/entities/permission";

import { CreateEditHearingFileTypeAttendantRole } from "./CreateEdit";

export interface IHearingFileTypeAttendantRoleItem {
  hearingFileTypeId: number;
  hearingFileTypeName: string;
  active: boolean;
  attendantRoleList: Array<IDefaultObject>;
}

export interface IDefaultObject {
  hearingFileTypeAttendantRoleId?: number;
  id: number;
  name: string;
  active: boolean;
  attendantRoleId: number;
  hearingFileTypeId: number;
}

export const HearingFileTypeAttendantRole = () => {
  const { t } = useTranslation();
  const [hearingFileTypeList, setHearingFileTypeList] = useState<
    Array<IDefaultObject>
  >([]);
  const [attendantRoleList, setAttendantRoleList] = useState<
    Array<IDefaultObject>
  >([]);

  const [search, setSearch] = useState("");
  const [situation, setSituation] = useState<any>(undefined);
  const [openModal, setOpenModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [element, setElement] = useState<any>();
  const [postOrPutTrue, setPostOrPutTrue] = useState<boolean>(false);
  const [hearingFileTypeId, setHearingFileTypeId] = useState<any>();

  const filteredData = hearingFileTypeList?.filter((item: any) =>
    item.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
  );

  const dispatch = useDispatch();
  const { addToast } = useToast();
  const customerId = LogOperation.getCustomerId();

  useEffect(() => {
    (async () => {
      setLoading(true);
      await onLoadList();
      setLoading(false);
    })();
  }, [search, situation]);

  async function onLoadList() {
    dispatch(showLoading());
    const response = await hearingFileTypeAttendantRole.getAll(
      customerId,
      situation,
    );

    if (response) {
      if (response.status) {
        setHearingFileTypeList(response.data.data);
      } else {
        addToast({
          type: "warning",
          title: response.message,
        });
      }
    }
    dispatch(hideLoading());
    return [];
  }

  function clear() {
    setIsEdit(false);
    setElement(undefined);
    setOpenModal(false);
  }

  async function onClickHearingFileType(item: IDefaultObject) {
    setIsEdit(true);

    const returnConsult =
      await transformHearingFileTypeAttendantRoleObject(item);

    setElement(returnConsult);
    setOpenModal(true);
  }

  async function transformHearingFileTypeAttendantRoleObject(
    HearingFileType: IDefaultObject,
  ) {
    setHearingFileTypeId(HearingFileType?.id);

    const consult =
      await hearingFileTypeAttendantRole.getAllRelationsHearingFileTypeAttendantRole(
        LogOperation.getCustomerId(),
        HearingFileType?.id,
      );

    const attendantRoleList = getAttendantRoleByConsult(consult.data.data);

    setAttendantRoleList(attendantRoleList);

    const returnConsult: IHearingFileTypeAttendantRoleItem = {
      hearingFileTypeId: HearingFileType.hearingFileTypeId,
      hearingFileTypeName: HearingFileType?.name,
      active: HearingFileType.active,
      attendantRoleList: getRelationshipHearingFileTypeWithAttendantRole(
        consult.data.data,
      ),
    };

    return returnConsult;
  }

  function getAttendantRoleByConsult(consult: Array<any>) {
    const listAttentandRole: Array<IDefaultObject> = [];

    consult.forEach((element) => {
      listAttentandRole.push({
        id: element.attendantRoleId,
        name: element.attendantRoleName,
        active: element.active,
        hearingFileTypeAttendantRoleId: element?.id,
        attendantRoleId: element.attendantRoleId,
        hearingFileTypeId: element.hearingFileTypeId,
      });
    });

    return listAttentandRole;
  }

  function getRelationshipHearingFileTypeWithAttendantRole(
    consult: Array<any>,
  ) {
    const listHearingFileTypeAttentandRole: Array<IDefaultObject> = [];
    consult.forEach((element) => {
      if (element.active === "TRUE") {
        listHearingFileTypeAttentandRole.push({
          id: element.attendantRoleId,
          name: element.attendantRoleName,
          active: element.active,
          hearingFileTypeAttendantRoleId: element?.id,
          attendantRoleId: element.attendantRoleId,
          hearingFileTypeId: element.hearingFileTypeId,
        });
      }
    });

    return listHearingFileTypeAttentandRole;
  }

  return (
    <>
      <AdminArea
        loading={loading}
        title={t("administration.hearingFileTypeAttendantRoles.title")}
        InputSearchLabel="Buscar por"
        InputSearchOnChange={(text) => setSearch(text)}
        list={filteredData}
        onClick={(item: any) => onClickHearingFileType(item)}
        setItemReturnClick={setElement}
        setOpenModal={setOpenModal}
        setIsEdit={setIsEdit}
        permissionName={FORMS_ADMIN.HEARING_FILE_TYPE_ATTENDANT_ROLE}
        onChangeSituation={(value: any) => setSituation(value)}
        showStatus={true}
        displayAtivoFilter={true}
        searchText={search}
      />
      <Drawer
        open={openModal}
        onClose={() => clear()}
        paddingLeft={33}
        paddingRight={33}
        title={
          isEdit
            ? t("administration.hearingFileTypeAttendantRoles.linkTitle")
            : t("administration.hearingFileTypeAttendantRoles.createLink")
        }
        onApply={() => setPostOrPutTrue(true)}
      >
        <CreateEditHearingFileTypeAttendantRole
          isEdit={isEdit}
          hearingFileTypeId={hearingFileTypeId}
          postOrPutTrue={postOrPutTrue}
          setPostOrPutTrue={setPostOrPutTrue}
          closeModal={openModal}
          setCloseModal={setOpenModal}
          hearingFileTypeList={hearingFileTypeList}
          attendantRoleList={attendantRoleList}
          hearingFileTypeAttendantRoleItem={element}
        />
      </Drawer>
    </>
  );
};
